import { useCallback, useEffect, useState } from 'react'
import { getUPIIntents } from '../../utils/appBridge'
import {
  getInstalledUpiAppsFromStorage,
  INSTALLED_UPI_INTENTS_UPDATED,
  InstalledUpiApp,
  InstalledUpiIntentsUpdatedEvent,
} from '../../utils/installedUpiIntents'

export type UseInstalledUpiIntentsOptions = {
  /** When false, skips storage read, native request, and event subscription. Defaults to true. */
  enabled?: boolean
  /** When false, does not call `getUPIIntents` on mount. Defaults to true. */
  requestOnMount?: boolean
}

export type UseInstalledUpiIntentsResult = {
  /** Parsed UPI apps; `null` only when `enabled` is false. Otherwise synced from storage on init, then native updates. */
  installedUpiApps: InstalledUpiApp[] | null
  /** Asks the native app to refresh installed UPI apps (Flutter writes via `setInstalledUpiIntents`). */
  requestUpiIntents: () => void
  /** Re-reads `installedUpiApps` from localStorage without calling native. */
  refreshFromStorage: () => void
}

/**
 * Loads and keeps installed UPI intent apps in sync for payment flows.
 *
 * On mount (when `enabled`):
 * 1. Hydrates from `localStorage` (`installedUpiApps`) using the same `convertToJSON` parser as Flutter payloads.
 * 2. Subscribes to `installedUpiIntentsUpdated` (dispatched when native calls `window.setInstalledUpiIntents`).
 * 3. Optionally requests a fresh list from the app via `getUPIIntents`.
 *
 * Use inside {@link BuyProvider} and {@link SIPProvider} so payment screens share one source of truth.
 *
 * @param options - Control mount behavior; both flags default to true.
 * @returns Installed apps state and helpers to refresh from native or storage.
 *
 * @example
 * ```tsx
 * const { installedUpiApps, requestUpiIntents } = useInstalledUpiIntents()
 * ```
 */
export function useInstalledUpiIntents(
  options: UseInstalledUpiIntentsOptions = {}
): UseInstalledUpiIntentsResult {
  const { enabled = true, requestOnMount = true } = options
  // Sync read on mount so Buy/SIP page switches do not treat “unknown” as “no intents”.
  const [installedUpiApps, setInstalledUpiApps] = useState<InstalledUpiApp[] | null>(() =>
    enabled ? getInstalledUpiAppsFromStorage() : null
  )

  const refreshFromStorage = useCallback(() => {
    setInstalledUpiApps(getInstalledUpiAppsFromStorage())
  }, [])

  const requestUpiIntents = useCallback(() => {
    getUPIIntents()
    if (enabled) {
      setInstalledUpiApps(getInstalledUpiAppsFromStorage())
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const onInstalledUpiIntentsUpdated = (event: Event) => {
      const apps = (event as InstalledUpiIntentsUpdatedEvent).detail
      setInstalledUpiApps(Array.isArray(apps) ? apps : getInstalledUpiAppsFromStorage())
    }

    refreshFromStorage()
    window.addEventListener(INSTALLED_UPI_INTENTS_UPDATED, onInstalledUpiIntentsUpdated)

    if (requestOnMount) {
      requestUpiIntents()
    }

    return () => {
      window.removeEventListener(INSTALLED_UPI_INTENTS_UPDATED, onInstalledUpiIntentsUpdated)
    }
  }, [enabled, requestOnMount, refreshFromStorage, requestUpiIntents])

  return {
    installedUpiApps,
    requestUpiIntents,
    refreshFromStorage,
  }
}
